import { MergeIcon } from 'components/icons';
import { Button } from 'components/ui/button';
import { ReactNode } from 'react';
import { MAX_MERGED_CONTACTS } from 'types/contactMerge';
import { PartyType } from 'types/contacts';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { Setting } from 'types/back-office-settings';
import { useSettings } from 'utils/useSettings';

const CONTACT_TYPE_PERMISSIONS = ['UPDATE.PARTIES.CONTACT_TYPE'];

type Props = {
    /** The ticked contacts' types, by contact id. */
    selected: ReadonlyMap<number, PartyType>;
    onClear: () => void;
};

/**
 * Why Merge is not offered for these types, or `null` when it is: two to ten contacts, of one type unless the
 * caller may convert one type to the other, which a merge of persons with companies does.
 */
const mergeHintId = (types: PartyType[], canMergeAcrossTypes: boolean): string | null => {
    if (types.length === 1) return 'contacts.merge.hint.one';
    if (types.length > MAX_MERGED_CONTACTS) return 'contacts.merge.hint.too-many';
    return canMergeAcrossTypes || new Set(types).size === 1 ? null : 'contacts.merge.hint.mixed';
};

/** Shown over the Contacts list while rows are ticked: the count, Merge or why it is missing, and Clear selection. */
const ContactsSelectionBar = ({ selected, onClear }: Props): ReactNode => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isContactTypeControlEnabled = useSettings().isSettingEnabled(Setting.CONTACT_TYPE_CONVERSION);
    const hasContactTypePermission = useHasPermissions(CONTACT_TYPE_PERMISSIONS);
    if (selected.size === 0) return null;
    const hintId = mergeHintId(Array.from(selected.values()), isContactTypeControlEnabled && hasContactTypePermission);

    return (
        <div className="bg-grey-50 flex flex-wrap items-center gap-3 rounded-md px-3 py-2">
            <span className="text-sm font-medium">
                {t('contacts.merge.selected', { count: String(selected.size) })}
            </span>
            {hintId ? (
                <span className="text-grey-600 text-sm">{t(hintId)}</span>
            ) : (
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigateWithReturn(`/contacts/merge?ids=${Array.from(selected.keys()).join(',')}`)}
                >
                    <MergeIcon />
                    {t('contacts.merge.action')}
                </Button>
            )}
            <Button variant="link" size="sm" className="text-link px-0" onClick={onClear}>
                {t('contacts.merge.clear-selection')}
            </Button>
        </div>
    );
};

export default ContactsSelectionBar;
